import {
  PermissionKeys,
  SetAgreementModel,
  useGetAgreementsByIdQuery,
  usePutAgreementsByIdMutation,
} from '@client/api';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  FormCancelButton,
  FormSubmitButton,
  indexValueSchema,
  intervalValueSchema,
  optionalIntervalValueSchema,
  openDateIntervalSchema,
  securityValueSchema,
  transformDateToIsoDateString,
  transformNaNToNull,
  transformToNullableObject,
} from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useState } from 'react';
import { DeepPartial, FieldPath, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { InferType, number, object, ObjectSchema, string } from 'yup';

import { CancelRequestModal } from '../../../../../components/cancel-request-modal/cancel-request-modal.component';

import {
  InvoicingPeriod,
  RentalPeriod,
  SecurityDeposit,
  IndexRegulation,
  VatInformation,
  Department,
} from './components';
import { AgreementPermissionBasedActionButton } from '../../../../../components/agreement-permission-based-button/agreement-permission-based-button';

type FormModel = Pick<
  SetAgreementModel,
  | 'duration'
  | 'invoicingPeriod'
  | 'invoicingStart'
  | 'index'
  | 'security'
  | 'vatInclusivePercentage'
  | 'departmentId'
  | 'optionLength'
  | 'notificationDeadlineMonths'
  | 'terminationNoticePeriodMonths'
>;

const schema = object({
  duration: openDateIntervalSchema.shape({
    start: string().transform(transformDateToIsoDateString).required(),
  }),
  invoicingStart: string().nullable().transform(transformDateToIsoDateString),
  invoicingPeriod: intervalValueSchema,
  index: indexValueSchema.nullable().makeOptional().transform(transformToNullableObject),
  security: securityValueSchema.nullable().makeOptional().transform(transformToNullableObject),
  vatInclusivePercentage: number().min(0).nullable().transform(transformNaNToNull),
  departmentId: number().nullable(),
  optionLength: optionalIntervalValueSchema.nullable().transform(transformToNullableObject) as any,
  notificationDeadlineMonths: number().min(0).nullable().transform(transformNaNToNull),
  terminationNoticePeriodMonths: number().min(0).nullable().transform(transformNaNToNull),
});

export type FormValues = InferType<typeof schema>;
export type FormPath = FieldPath<FormModel>;
type FormDefaultValues = DeepPartial<FormValues>;

export interface AgreementConditionsCardProps {
  className?: string;
}

export function AgreementConditionsCard({ className }: AgreementConditionsCardProps) {
  const { t, ready } = useTranslation('agreements');
  const { t: tCommon } = useTranslation();
  const { t: tAgreements } = useTranslation('agreements');

  const { id } = useParams<{ id: string }>();
  const { data: agreement, isFetching } = useGetAgreementsByIdQuery({ id: id ?? '' }, { skip: id == null });

  const isTerminated = agreement?.status === 'Terminated';

  const [putAgreement] = usePutAgreementsByIdMutation();

  const [editing, setEditing] = useState<boolean>(false);

  const handleOpenEdit = () => {
    setEditing(true);
  };

  const onSubmit = async (data: FormValues) => {
    if (id == null || agreement == null) return;

    try {
      await putAgreement({
        id: agreement.id,
        body: {
          ...agreement,
          ...data,
        },
      }).unwrap();
    } catch (e) {
      return;
    }

    setEditing(false);
  };

  const defaultValues: FormDefaultValues = useMemo(
    () => ({
      ...agreement,
      optionLength:
        agreement?.optionLength?.count != null
          ? {
              count: agreement.optionLength.count,
              type: agreement.optionLength.type ?? 'Month',
            }
          : {
              count: undefined,
              type: undefined,
            },
    }),
    [agreement],
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    formState: { isDirty, errors, isSubmitting },
    reset,
    control,
    setValue,
  } = methods;

  const [cancelingRequest, setCancelingRequest] = useState<boolean>(false);

  const checkCancelRequest = () => {
    if (isDirty) {
      setCancelingRequest(true);
    } else {
      setEditing(false);
    }
  };

  const handleConfirmCancelRequest = () => {
    setEditing(false);
    setCancelingRequest(false);
  };

  const handleCancelCancelRequest = () => {
    setCancelingRequest(false);
  };

  useEffect(() => {
    if (!editing) {
      reset(defaultValues);
    }
  }, [defaultValues, editing, reset]);

  return (
    <>
      <Card className={className}>
        <FormProvider {...methods}>
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            {/* Headline */}
            <CardHeader>
              <CardTitle className="flex-1" loading={!ready} variant="highlighted">
                {t('agreement.conditions')}
              </CardTitle>
              <CardActions>
                {!editing ? (
                  <AgreementPermissionBasedActionButton
                    agreementId={id ?? ''}
                    permissions={[PermissionKeys.Agreement.Update]}
                    onClick={handleOpenEdit}
                    disabled={isFetching || id == null || agreement == null || isTerminated}
                    disabledTooltip={isTerminated ? tAgreements('terminatedAgreementEditDisabled') : undefined}
                    title={tCommon('edit')}
                  />
                ) : (
                  <>
                    <FormCancelButton size="sm" onClick={checkCancelRequest} color="primary-dark">
                      {tCommon('cancel')}
                    </FormCancelButton>
                    <FormSubmitButton type="submit" size="sm" disabled={isFetching} color="primary">
                      {tCommon('save')}
                    </FormSubmitButton>
                  </>
                )}
              </CardActions>
            </CardHeader>

            {/* Content */}
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-md border border-border p-4 space-y-2">
                  <RentalPeriod
                    isFetching={isFetching}
                    startDate={agreement?.duration?.start}
                    endDate={agreement?.duration?.end}
                    editing={editing}
                    optionLength={agreement?.optionLength}
                    notificationDeadlineMonths={agreement?.notificationDeadlineMonths}
                    terminationNoticePeriodMonths={agreement?.terminationNoticePeriodMonths}
                  />
                  <InvoicingPeriod
                    isFetching={isFetching}
                    editing={editing}
                    setValue={setValue}
                    errors={errors}
                    invoicingStart={agreement?.invoicingStart}
                    invoicingPeriod={agreement?.invoicingPeriod}
                    isSubmitting={isSubmitting}
                  />
                  <SecurityDeposit
                    isFetching={isFetching}
                    editing={editing}
                    control={control}
                    securityType={agreement?.security?.type}
                    securityAmount={agreement?.security?.amount?.amount}
                    securityCurrencyCode={agreement?.security?.amount?.currency?.code as any}
                  />
                </div>
                <div className="rounded-md border border-border p-4 space-y-2">
                  <IndexRegulation
                    isFetching={isFetching}
                    editing={editing}
                    setValue={setValue}
                    errors={errors}
                    adjustmentPerInterval={agreement?.index?.adjustmentPerInterval}
                    indexStartDate={agreement?.index?.startDate}
                    indexInterval={agreement?.index?.interval}
                    indexType={(agreement?.index as any)?.type}
                    isSubmitting={isSubmitting}
                    agreementStartDate={agreement?.duration?.start}
                    baseIndexDate={(agreement?.index as any)?.baseIndexDate}
                    baseIndexValue={(agreement?.index as any)?.baseIndexValue}
                  />
                  <VatInformation
                    isFetching={isFetching}
                    editing={editing}
                    vatInclusivePercentage={agreement?.vatInclusivePercentage}
                  />
                  <Department
                    isFetching={isFetching}
                    editing={editing}
                    departmentId={agreement?.departmentId}
                    managerId={agreement?.parties?.find((party) => party.type === 'Manager')?.stakeholder?.companyId}
                  />
                </div>
              </div>
            </CardContent>
          </form>
        </FormProvider>
      </Card>

      <CancelRequestModal
        open={cancelingRequest}
        onConfirm={handleConfirmCancelRequest}
        onCancel={handleCancelCancelRequest}
      />
    </>
  );
}
